import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../config/axiosConfig";
import config from "../../config/api";
import { setRealtimeEvent } from "../../realtime/realtimeSlice";
import {
  addItemToList,
  getRealtimeEntityFromPayload,
  isModuleMatch,
  removeItemFromList,
  updateItemInList,
} from "../../realtime/realtimeUtils";

const API_URL = `${config.backendUrl}/descriptiontask`;

export const replaceDescriptionTasksByProjectWithCreatedBy = createAsyncThunk(
  "descriptionTasks/replaceByProjectWithCreatedBy",
  async ({ tasks, createdBy }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${API_URL}/replace-by-project`, {
        tasks,
        createdBy
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const replaceDescriptionTasksByProject = createAsyncThunk(
  "descriptionTasks/replaceByProject",
  async (taskArray, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${API_URL}/saveupdate`, taskArray);
      return response.data.data; // assuming { message, data } is returned
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


export const fetchDescriptionTasks = createAsyncThunk(
  "descriptionTasks/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(API_URL);
      return response.data.data; // assuming response = { message, data }
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchDescriptionTasksProjectTracking = createAsyncThunk(
  "descriptionTasks/fetchProjectTracking",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${API_URL}/getall-project-track`);
      return response.data.data; // assuming response = { message, data }
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createDescriptionTasks = createAsyncThunk(
  "descriptionTasks/create",
  async (taskArray, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(API_URL, taskArray);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateDescriptionTask = createAsyncThunk(
  "descriptionTasks/update",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`${API_URL}/update/${id}`, updatedData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteDescriptionTask = createAsyncThunk(
  "descriptionTasks/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${API_URL}/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const descriptionTaskSlice = createSlice({
  name: "descriptionTasks",
  initialState: {
    tasks: [],
    descriptionProjectTracking: [],
    loading: false,
    error: null,
  },
  reducers: {
    resetDescriptionTaskState: (state) => {
      state.loading = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder

    .addCase(fetchDescriptionTasks.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDescriptionTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = action.payload;
      })
      .addCase(fetchDescriptionTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchDescriptionTasksProjectTracking.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDescriptionTasksProjectTracking.fulfilled, (state, action) => {
        state.loading = false;
        state.descriptionProjectTracking = action.payload;
      })
      .addCase(fetchDescriptionTasksProjectTracking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(createDescriptionTasks.pending, (state) => {
        state.loading = true;
      })
      .addCase(createDescriptionTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks.push(...action.payload);
      })
      .addCase(createDescriptionTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateDescriptionTask.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateDescriptionTask.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.tasks.findIndex((task) => task._id === action.payload._id);
        if (index !== -1) {
          state.tasks[index] = action.payload;
        }
      })
      .addCase(updateDescriptionTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteDescriptionTask.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteDescriptionTask.fulfilled, (state, action) => {
        state.loading = false;
        state.tasks = state.tasks.filter((task) => task._id !== action.payload);
      })
      .addCase(deleteDescriptionTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(replaceDescriptionTasksByProject.pending, (state) => {
        state.loading = true;
      })
      .addCase(replaceDescriptionTasksByProject.fulfilled, (state, action) => {
        state.loading = false;
        const updatedProjectName = action.payload[0]?.projectname;
        state.tasks = state.tasks.filter(task => task.projectname !== updatedProjectName);
        state.tasks.push(...action.payload);
      })
      .addCase(replaceDescriptionTasksByProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(replaceDescriptionTasksByProjectWithCreatedBy.pending, (state) => {
        state.loading = true;
      })
      .addCase(replaceDescriptionTasksByProjectWithCreatedBy.fulfilled, (state, action) => {
        state.loading = false;
        const updatedProjectName = action.payload[0]?.projectname;
        const updatedCreatedBy = action.payload[0]?.createdBy;
        state.tasks = state.tasks.filter(
          (task) =>
            !(task.projectname === updatedProjectName && task.createdBy === updatedCreatedBy)
        );
        state.tasks.push(...action.payload);
      })
      .addCase(replaceDescriptionTasksByProjectWithCreatedBy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(setRealtimeEvent, (state, action) => {
        const { module, action: eventAction, data } = action.payload || {};
        if (!isModuleMatch(module, ["descriptiontask"])) return;

        const incomingItem = getRealtimeEntityFromPayload(data);
        if (!incomingItem) return;

        const applyListUpdate = (listKey) => {
          if (eventAction === "updated") {
            state[listKey] = updateItemInList(state[listKey], incomingItem);
          }
          if (eventAction === "created") {
            state[listKey] = addItemToList(state[listKey], incomingItem);
          }
          if (eventAction === "deleted") {
            state[listKey] = removeItemFromList(state[listKey], incomingItem);
          }
        };

        applyListUpdate("tasks");
        applyListUpdate("descriptionProjectTracking");
      });
  }
});

export const { resetDescriptionTaskState } = descriptionTaskSlice.actions;
export default descriptionTaskSlice.reducer;